import {
  ChoiceGroup,
  DefaultButton,
  Dialog,
  DialogFooter,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  TextField,
  TooltipHost
} from "@fluentui/react";
import React from "react";
import { AI_PLAYGROUND_CONSTANTS } from "../../AIPlayground.constants";
import { playgroundStyles as styles } from "../../Styles";
import { playgroundClassNames as classNames } from "../../Styles/AIPlayground.styles";
import { SaveSessionDialogProps } from "../../Interfaces/PromptPlayground.interfaces";
import { createChoiceFieldRenderer } from "../Common";

export const SaveSessionDialog: React.FC<SaveSessionDialogProps> = ({
  isOpen,
  onClose,
  onDialogInputChange,
  onSave,
  isSaving,
  isSaved,
  inputData,
  fieldsFocused,
  setFieldsFocused
}) => {
  const isFormValid = React.useMemo(() => {
    return inputData.title.trim() && inputData.description.trim();
  }, [inputData.title, inputData.description]);
  const scheduleOptions = React.useMemo(
    () =>
      AI_PLAYGROUND_CONSTANTS.SCHEDULE_OPTIONS.map((option) => ({
        key: option.key,
        text: option.text,
        onRenderField: createChoiceFieldRenderer(classNames.scaledChoiceFieldStyle)
      })),
    [classNames.scaledChoiceFieldStyle]
  );
  const frequencyOptions = React.useMemo(
    () =>
      AI_PLAYGROUND_CONSTANTS.SCHEDULE_FREQUENCY_OPTIONS.map((f) => ({
        key: f.key,
        text: f.text,
        onRenderField: createChoiceFieldRenderer(classNames.scaledChoiceFieldStyle)
      })),
    [classNames.scaledChoiceFieldStyle]
  );

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onClose}
      minWidth={600}
      styles={styles.dialogStyles}
      modalProps={{
        styles: {
          scrollableContent: {
            overflow: "hidden"
          }
        }
      }}
      dialogContentProps={{
        title: "Save Session",
        styles: styles.dialogContentStyles
      }}
    >
      <div className={classNames.flexRowWithGap}>
        <TextField
          label="Title"
          styles={styles.textFieldStyles}
          onChange={(_, value) => onDialogInputChange("title", value ?? "")}
          resizable={false}
          value={inputData.title}
          required
          onBlur={() => setFieldsFocused((prev) => ({ ...prev, title: true }))}
          errorMessage={fieldsFocused.title && !inputData.title.trim() ? "Title is required" : ""}
        />
        <TextField
          label="Description"
          multiline
          rows={4}
          styles={styles.textFieldStyles}
          onChange={(_, value) => onDialogInputChange("description", value ?? "")}
          resizable={false}
          value={inputData.description}
          required
          onBlur={() => setFieldsFocused((prev) => ({ ...prev, description: true }))}
          errorMessage={fieldsFocused.description && !inputData.description.trim() ? "Description is required" : ""}
        />
        <TooltipHost content="Kusto query is not editable. Edit your query before saving the session.">
          <TextField
            label="Kusto Query"
            readOnly
            multiline
            resizable={false}
            rows={7}
            styles={styles.readonlyTextFieldStyles}
            value={inputData.query}
          />
        </TooltipHost>
        <TooltipHost content="Prompt is not editable. Edit your prompt before saving the session.">
          <TextField
            label="Prompt"
            readOnly
            multiline
            resizable={false}
            rows={7}
            styles={styles.readonlyTextFieldStyles}
            value={inputData.prompt}
          />
        </TooltipHost>
        <ChoiceGroup
          label="Would you like to schedule this prompt?"
          options={scheduleOptions}
          styles={styles.choiceGroupStyles}
          selectedKey={inputData.isActiveSchedule === true ? "yes" : "no"}
          onChange={(_, option) => onDialogInputChange("isActiveSchedule", option?.key === "yes")}
          name="isActiveSchedule"
        />
        {inputData.isActiveSchedule === true ? (
          <ChoiceGroup
            label="Choose schedule frequency"
            options={frequencyOptions}
            styles={styles.choiceGroupStyles}
            selectedKey={inputData.scheduleFrequency}
            onChange={(_, option) => onDialogInputChange("scheduleFrequency", option?.key ?? "")}
            name="scheduleFrequency"
          />
        ) : null}
      </div>
      <DialogFooter styles={styles.dialogFooterStyles}>
        <PrimaryButton
          iconProps={{ iconName: "Save" }}
          onClick={onSave}
          disabled={isSaving || isSaved || !isFormValid}
          styles={styles.buttonStyles}
        >
          {(() => {
            if (isSaving)
              return (
                <>
                  <span style={{ marginRight: 8 }}>Saving</span>
                  <Spinner size={SpinnerSize.xSmall} />;
                </>
              );
            if (isSaved) return <>Saved successfully</>;
            return <>Save</>;
          })()}
        </PrimaryButton>
        <DefaultButton
          iconProps={{ iconName: "Cancel" }}
          onClick={onClose}
          text={isSaved ? "Close" : "Cancel"}
          disabled={isSaving}
          styles={styles.defaultButtonStyles}
        />
      </DialogFooter>
    </Dialog>
  );
};
